import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  FaSave,
  FaTimes,
  FaEye,
  FaEdit,
  FaPlus,
  FaSpinner,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaVideo,
  FaTrash,
  FaUpload,
  FaPlay,
  FaFolder,
  FaFolderOpen,
  FaPencilAlt,
  FaChevronRight,
  FaChevronDown
} from 'react-icons/fa';
import { MdLibraryBooks, MdPreview } from 'react-icons/md';
import RichTextEditor from '../../components/RichTextEditor';
import { useNavigate } from 'react-router-dom';
import './ManualEditor.css';

const api = axios.create({
    baseURL: 'https://uk.backend.hms.hiradentallab.com/api',
    headers: { 'Content-Type': 'application/json' }
});

const ManualEditor = () => {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [videos, setVideos] = useState([]);
    const [videoFolders, setVideoFolders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [existingManual, setExistingManual] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [videoTitle, setVideoTitle] = useState('');
    const [videoDescription, setVideoDescription] = useState('');
    const [selectedFolderId, setSelectedFolderId] = useState('');
    const [showVideoUpload, setShowVideoUpload] = useState(false);

    // Folder management states
    const [showFolderForm, setShowFolderForm] = useState(false);
    const [folderName, setFolderName] = useState('');
    const [folderDescription, setFolderDescription] = useState('');
    const [editingFolderId, setEditingFolderId] = useState(null);

    // Video editing states
    const [editingVideoId, setEditingVideoId] = useState(null);
    const [editVideoTitle, setEditVideoTitle] = useState('');
    const [editVideoDescription, setEditVideoDescription] = useState('');
    const [editVideoFolderId, setEditVideoFolderId] = useState('');

    // Folder navigation states
    const [expandedFolders, setExpandedFolders] = useState({});
    const [activeFolderId, setActiveFolderId] = useState('all'); // 'all', 'uncategorized', or folder ID

    useEffect(() => {
        fetchManual();
    }, []);

    const fetchManual = async () => {
        try {
            const response = await api.get('/manuals');

            if (response.data.success && response.data.data) {
                const manual = response.data.data;
                setTitle(manual.title);
                setContent(manual.content);
                setVideos(manual.videos || []);
                setVideoFolders(manual.videoFolders || []);
                setExistingManual(manual);
                setIsEditing(true);

                // Initialize expanded folders
                const expandedState = {};
                manual.videoFolders?.forEach(folder => {
                    expandedState[folder._id] = true;
                });
                setExpandedFolders(expandedState);
            }
        } catch (error) {
            console.error('Error fetching manual:', error);
            if (error.response?.status === 404) {
            }
        } finally {
            setFetching(false);
        }
    };

    // Get filtered videos based on active folder
    const getFilteredVideos = () => {
        if (activeFolderId === 'all') {
            return videos;
        } else if (activeFolderId === 'uncategorized') {
            return videos.filter(v => !v.folderId || v.folderId === null);
        } else {
            return videos.filter(v => v.folderId === activeFolderId);
        }
    };

    // Toggle folder expansion
    const toggleFolder = (folderId) => {
        setExpandedFolders(prev => ({
            ...prev,
            [folderId]: !prev[folderId]
        }));
    };

    // Folder Management Functions
    const handleCreateFolder = async () => {
        if (!folderName.trim()) {
            showWarningAlert('Please enter a folder name');
            return;
        }

        try {
            const response = await api.post('/manuals/folders', {
                name: folderName,
                description: folderDescription
            });

            if (response.data.success) {
                await fetchManual();
                setFolderName('');
                setFolderDescription('');
                setShowFolderForm(false);
                showSuccessAlert('Folder created successfully!');
            }
        } catch (error) {
            console.error('Error creating folder:', error);
            showErrorAlert(error.response?.data?.message || 'Error creating folder');
        }
    };

    const handleUpdateFolder = async (folderId) => {
        if (!folderName.trim()) {
            showWarningAlert('Please enter a folder name');
            return;
        }

        try {
            const response = await api.put(`/manuals/folders/${folderId}`, {
                name: folderName,
                description: folderDescription
            });

            if (response.data.success) {
                await fetchManual();
                setFolderName('');
                setFolderDescription('');
                setEditingFolderId(null);
                setShowFolderForm(false);
                showSuccessAlert('Folder updated successfully!');
            }
        } catch (error) {
            console.error('Error updating folder:', error);
            showErrorAlert(error.response?.data?.message || 'Error updating folder');
        }
    };

    const handleDeleteFolder = async (folderId) => {
        const result = await Swal.fire({
            title: 'Delete Folder',
            text: 'Are you sure you want to delete this folder? Videos in this folder will be moved to "Uncategorized".',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await api.delete(`/manuals/folders/${folderId}`);
                if (response.data.success) {
                    await fetchManual();
                    if (activeFolderId === folderId) {
                        setActiveFolderId('all');
                    }
                    showSuccessAlert('Folder deleted successfully!');
                }
            } catch (error) {
                console.error('Error deleting folder:', error);
                showErrorAlert(error.response?.data?.message || 'Error deleting folder');
            }
        }
    };

    const startEditFolder = (folder) => {
        setEditingFolderId(folder._id);
        setFolderName(folder.name);
        setFolderDescription(folder.description || '');
        setShowFolderForm(true);
    };

    // Video Management Functions
    const handleVideoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!videoTitle.trim()) {
            showWarningAlert('Please enter a video title');
            return;
        }

        // Video validation (keeping your existing validation)
        const allowedVideoTypes = [
            'video/mp4', 'video/webm', 'video/x-matroska', 'video/x-mkv',
            'video/quicktime', 'video/x-msvideo', 'video/avi', 'video/msvideo',
            'video/mpeg', 'video/ogg', 'video/3gpp', 'video/3gpp2',
            'video/x-flv', 'video/flv', 'video/mov', 'video/wmv',
            'video/x-ms-wmv', 'video/m4v', 'video/x-m4v',
            'application/octet-stream', 'application/vnd.ms-asf',
            'video/x-ms-asf', 'video/x-matroska', 'video/divx', 'video/x-divx'
        ];

        const allowedExtensions = [
            '.mp4', '.m4v', '.webm', '.mkv', '.avi', '.mov',
            '.wmv', '.flv', '.mpeg', '.mpg', '.3gp', '.3g2',
            '.ogv', '.ogg', '.ts', '.mts', '.m2ts', '.vob',
            '.divx', '.f4v', '.rm', '.rmvb', '.asf'
        ];

        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        const hasValidExtension = allowedExtensions.includes(fileExtension);
        const hasValidMimeType = allowedVideoTypes.includes(file.type) || file.type.startsWith('video/');

        if (!hasValidExtension && !hasValidMimeType) {
            showErrorAlert('Please upload a valid video file. Supported formats: MP4, MKV, AVI, MOV, WMV, FLV, WebM, etc.');
            return;
        }

        if (file.size > 500 * 1024 * 1024) {
            showErrorAlert('Video size must be less than 500MB');
            return;
        }

        const formData = new FormData();
        formData.append('video', file);
        formData.append('title', videoTitle);
        formData.append('description', videoDescription);
        if (selectedFolderId) {
            formData.append('folderId', selectedFolderId);
        }

        setUploadingVideo(true);

        try {
            const response = await api.post('/manuals/upload-video', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                await fetchManual();
                setVideoTitle('');
                setVideoDescription('');
                setSelectedFolderId('');
                setShowVideoUpload(false);
                showSuccessAlert('Video uploaded successfully!');
            }
        } catch (error) {
            console.error('Error uploading video:', error);
            showErrorAlert(error.response?.data?.message || 'Error uploading video');
        } finally {
            setUploadingVideo(false);
            e.target.value = '';
        }
    };

    const handleUpdateVideo = async (videoId) => {
        if (!editVideoTitle.trim()) {
            showWarningAlert('Please enter a video title');
            return;
        }

        try {
            const response = await api.put(`/manuals/video/${videoId}`, {
                title: editVideoTitle,
                description: editVideoDescription,
                folderId: editVideoFolderId || null
            });

            if (response.data.success) {
                await fetchManual();
                setEditingVideoId(null);
                setEditVideoTitle('');
                setEditVideoDescription('');
                setEditVideoFolderId('');
                showSuccessAlert('Video updated successfully!');
            }
        } catch (error) {
            console.error('Error updating video:', error);
            showErrorAlert(error.response?.data?.message || 'Error updating video');
        }
    };

    const startEditVideo = (video) => {
        setEditingVideoId(video._id);
        setEditVideoTitle(video.title);
        setEditVideoDescription(video.description || '');
        setEditVideoFolderId(video.folderId || '');
    };

    const handleDeleteVideo = async (videoId) => {

        const result = await Swal.fire({
            title: 'Delete Video',
            text: 'Are you sure you want to delete this video?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await api.delete(`/manuals/video/${videoId}`);
                if (response.data.success) {
                    setVideos(response.data.data.videos || []);
                    showSuccessAlert('Video deleted successfully!');
                    await fetchManual();
                } else {
                    showErrorAlert('Failed to delete video');
                }
            } catch (error) {
                console.error('Error deleting video:', error);
                showErrorAlert(error.response?.data?.message || 'Error deleting video');
            }
        }
    };

    // Helper function to get folder name by ID
    const getFolderName = (folderId) => {
        if (!folderId) return 'Uncategorized';
        const folder = videoFolders.find(f => f._id === folderId);
        return folder ? folder.name : 'Uncategorized';
    };

    // Alert functions (keeping your existing ones)
    const showSuccessAlert = (message, isUpdate = false) => {
        Swal.fire({
            title: isUpdate ? 'Manual Updated' : 'Success',
            text: message,
            icon: 'success',
            confirmButtonColor: '#004aad',
            confirmButtonText: 'OK',
            timer: 3000,
            timerProgressBar: true,
            backdrop: true,
            allowOutsideClick: false,
            customClass: {
                popup: 'me-professional-swal',
                title: 'me-swal-title',
                confirmButton: 'me-swal-confirm-btn'
            }
        });
    };

    const showErrorAlert = (message) => {
        Swal.fire({
            title: 'Operation Failed',
            text: message,
            icon: 'error',
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Try Again',
            customClass: {
                popup: 'me-professional-swal',
                confirmButton: 'me-swal-error-btn'
            }
        });
    };

    const showWarningAlert = (message) => {
        Swal.fire({
            title: 'Missing Information',
            text: message,
            icon: 'warning',
            confirmButtonColor: '#ffc107',
            confirmButtonText: 'Got it',
            customClass: {
                popup: 'me-professional-swal'
            }
        });
    };

    const showPreviewModal = () => {
        if (!title.trim() && !content.trim()) {
            showWarningAlert('Please add content before previewing');
            return;
        }

        Swal.fire({
            title: title.trim() || 'Untitled Manual',
            html: `
                <div class="me-preview-modal-container">
                    <div class="me-preview-meta">
                        <div class="me-preview-meta-item">
                            <span class="me-meta-label">Status</span>
                            <span class="me-meta-value me-status-draft">Draft</span>
                        </div>
                        <div class="me-preview-meta-item">
                            <span class="me-meta-label">Last Modified</span>
                            <span class="me-meta-value">${new Date().toLocaleString()}</span>
                        </div>
                    </div>
                    <div class="me-preview-content">
                        ${content || '<p class="me-empty-preview">No content available for preview</p>'}
                    </div>
                </div>
            `,
            width: '900px',
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: 'Edit',
            cancelButtonText: 'Close',
            confirmButtonColor: '#004aad',
            cancelButtonColor: '#6c757d',
            customClass: {
                popup: 'me-preview-swal',
                confirmButton: 'me-preview-edit-btn',
                cancelButton: 'me-preview-close-btn'
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title.trim()) {
            showWarningAlert('Please enter a title for the manual');
            return;
        }

        if (!content.trim()) {
            showWarningAlert('Please enter content for the manual');
            return;
        }

        setLoading(true);

        try {
            const response = await api.post('/manuals', {
                title: title.trim(),
                content: content,
                videos: videos
            });

            if (response.data.success) {
                const message = response.data.isUpdate
                    ? 'Your manual has been successfully updated'
                    : 'Your manual has been successfully created';

                setIsEditing(true);
                setExistingManual(response.data.data);
                showSuccessAlert(message, response.data.isUpdate);
                window.dispatchEvent(new CustomEvent('manualUpdated'));
            }
        } catch (error) {
            console.error('Error saving manual:', error);
            let errorMessage = 'An unexpected error occurred while saving the manual';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            showErrorAlert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (isEditing && (title !== existingManual?.title || content !== existingManual?.content)) {
            Swal.fire({
                title: 'Unsaved Changes',
                text: 'You have unsaved changes. Are you sure you want to leave?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#dc3545',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Leave',
                cancelButtonText: 'Stay',
                customClass: {
                    confirmButton: 'me-swal-leave-btn'
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/admin/manuals');
                }
            });
        } else {
            navigate('/admin/manuals');
        }
    };

    if (fetching) {
        return (
            <div className="me-manual-editor">
                <div className="me-container">
                    <div className="me-loading-state">
                        <FaSpinner className="me-loading-spinner-icon" />
                        <p>Loading manual content...</p>
                    </div>
                </div>
            </div>
        );
    }

    const filteredVideos = getFilteredVideos();
    const uncategorizedVideos = videos.filter(v => !v.folderId || v.folderId === null);

    return (
        <div className="me-manual-editor">
            <div className="me-container">
                <div className="me-editor-header">
                    <div className="me-header-left">
                        <div className="me-header-icon">
                            <MdLibraryBooks />
                        </div>
                        <div>
                            <h1>{isEditing ? 'Edit Manual' : 'Create New Manual'}</h1>
                        </div>
                    </div>
                    {isEditing && (
                        <div className="me-header-right">
                            <div className="me-last-edited">
                                Last edited: {existingManual?.updatedAt ? new Date(existingManual.updatedAt).toLocaleString() : 'Just now'}
                            </div>
                        </div>
                    )}
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="me-form-group">
                        <label htmlFor="title">
                            Manual Title
                            <span className="me-required">*</span>
                        </label>
                        <input
                            type="text"
                            id="title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Enter a descriptive title for your manual"
                            required
                            autoFocus
                            className="me-title-input"
                        />
                    </div>

                    <div className="me-form-group">
                        <label>
                            Content
                            <span className="me-required">*</span>
                        </label>
                        <RichTextEditor
                            initialContent={content}
                            onChange={setContent}
                            placeholder="Start writing your manual content here..."
                        />
                    </div>

                    {/* Folder and Video Management Section */}
                    <div className="me-form-group">
                        <div className="me-video-management-header">
                            <label>
                                Video Management
                                <span className="me-optional">(Optional)</span>
                            </label>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowFolderForm(true);
                                    setEditingFolderId(null);
                                    setFolderName('');
                                    setFolderDescription('');
                                }}
                                className="me-btn-add-folder"
                            >
                                <FaFolderOpen /> Create New Folder
                            </button>
                        </div>

                        {/* Folder Form */}
                        {showFolderForm && (
                            <div className="me-folder-form">
                                <h4>{editingFolderId ? 'Edit Folder' : 'Create New Folder'}</h4>
                                <input
                                    type="text"
                                    placeholder="Folder Name *"
                                    value={folderName}
                                    onChange={(e) => setFolderName(e.target.value)}
                                    className="me-folder-name-input"
                                />
                                <textarea
                                    placeholder="Folder Description (Optional)"
                                    value={folderDescription}
                                    onChange={(e) => setFolderDescription(e.target.value)}
                                    className="me-folder-description-input"
                                    rows="2"
                                />
                                <div className="me-folder-form-actions">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (editingFolderId) {
                                                handleUpdateFolder(editingFolderId);
                                            } else {
                                                handleCreateFolder();
                                            }
                                        }}
                                        className="me-btn-folder-save"
                                    >
                                        <FaSave /> {editingFolderId ? 'Update Folder' : 'Create Folder'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowFolderForm(false);
                                            setEditingFolderId(null);
                                            setFolderName('');
                                            setFolderDescription('');
                                        }}
                                        className="me-btn-folder-cancel"
                                    >
                                        <FaTimes /> Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Folder Navigation Tree */}
                        <div className="me-folder-navigation">
                            <div
                                className={`me-folder-nav-item ${activeFolderId === 'all' ? 'active' : ''}`}
                                onClick={() => setActiveFolderId('all')}
                            >
                                <FaFolderOpen className="me-folder-nav-icon" />
                                <span>All Videos ({videos.length})</span>
                            </div>

                            {videoFolders.map((folder) => {
                                const folderVideos = videos.filter(v => v.folderId === folder._id);
                                const isExpanded = expandedFolders[folder._id];

                                return (
                                    <div key={folder._id} className="me-folder-nav-group">
                                        <div
                                            className={`me-folder-nav-item ${activeFolderId === folder._id ? 'active' : ''}`}
                                            onClick={() => {
                                                setActiveFolderId(folder._id);
                                                toggleFolder(folder._id);
                                            }}
                                        >
                                            {isExpanded ?
                                                <FaChevronDown className="me-folder-chevron" /> :
                                                <FaChevronRight className="me-folder-chevron" />
                                            }
                                            <FaFolder className="me-folder-nav-icon" />
                                            <span>{folder.name} ({folderVideos.length})</span>
                                            <div className="me-folder-nav-actions" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    type="button"
                                                    onClick={() => startEditFolder(folder)}
                                                    className="me-btn-folder-edit-small"
                                                    title="Edit folder"
                                                >
                                                    <FaPencilAlt />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteFolder(folder._id)}
                                                    className="me-btn-folder-delete-small"
                                                    title="Delete folder"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </div>

                                        {isExpanded && activeFolderId === folder._id && folderVideos.length > 0 && (
                                            <div className="me-folder-videos">
                                                {folderVideos.map((video) => (
                                                    <div key={video._id} className="me-folder-video-item">
                                                        <FaVideo className="me-video-icon-small" />
                                                        <span>{video.title}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {uncategorizedVideos.length > 0 && (
                                <div
                                    className={`me-folder-nav-item ${activeFolderId === 'uncategorized' ? 'active' : ''}`}
                                    onClick={() => setActiveFolderId('uncategorized')}
                                >
                                    <FaFolder className="me-folder-nav-icon" />
                                    <span>Uncategorized ({uncategorizedVideos.length})</span>
                                </div>
                            )}
                        </div>

                        {/* Video List based on active folder */}
                        <div className="me-videos-list">
                            <div className="me-videos-list-header">
                                <h4>
                                    {activeFolderId === 'all' ? 'All Videos' :
                                     activeFolderId === 'uncategorized' ? 'Uncategorized Videos' :
                                     `Videos in ${getFolderName(activeFolderId)}`} ({filteredVideos.length})
                                </h4>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowVideoUpload(!showVideoUpload);
                                        setSelectedFolderId(activeFolderId !== 'all' && activeFolderId !== 'uncategorized' ? activeFolderId : '');
                                    }}
                                    className="me-btn-add-video"
                                >
                                    <FaPlus /> Add Video
                                </button>
                            </div>

                            {/* Video Upload Section */}
                            {showVideoUpload && (
                                <div className="me-video-upload-section">
                                    <h4>Upload New Video</h4>
                                    <div className="me-video-upload-form">
                                        <input
                                            type="text"
                                            placeholder="Video Title *"
                                            value={videoTitle}
                                            onChange={(e) => setVideoTitle(e.target.value)}
                                            className="me-video-title-input"
                                        />
                                        <textarea
                                            placeholder="Video Description (Optional)"
                                            value={videoDescription}
                                            onChange={(e) => setVideoDescription(e.target.value)}
                                            className="me-video-description-input"
                                            rows="2"
                                        />
                                        <select
                                            value={selectedFolderId}
                                            onChange={(e) => setSelectedFolderId(e.target.value)}
                                            className="me-video-folder-select"
                                        >
                                            <option value="">Select Folder (Optional)</option>
                                            {videoFolders.map(folder => (
                                                <option key={folder._id} value={folder._id}>
                                                    {folder.name}
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            type="file"
                                            accept="video/*,.mkv,.avi,.mov,.wmv,.flv,.mpeg,.mpg,.3gp,.3g2,.ogv,.ogg,.ts,.mts,.m2ts,.vob,.divx,.f4v,.rm,.rmvb,.asf,.m4v,.webm"
                                            onChange={handleVideoUpload}
                                            className="me-video-file-input"
                                            id="me-video-upload"
                                        />
                                        <label htmlFor="me-video-upload" className="me-video-file-label">
                                            <FaUpload /> Choose Video File
                                        </label>
                                        <small className="me-upload-hint">
                                            Supported formats: MP4, MKV, AVI, MOV, WMV, FLV, WebM, and more (Max 500MB)
                                        </small>
                                        {uploadingVideo && (
                                            <div className="me-uploading-progress">
                                                <FaSpinner className="me-spinning" />
                                                <span>Uploading video...</span>
                                            </div>
                                        )}
                                        <div className="me-video-upload-actions">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowVideoUpload(false);
                                                    setVideoTitle('');
                                                    setVideoDescription('');
                                                    setSelectedFolderId('');
                                                }}
                                                className="me-btn-cancel-upload"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Filtered Video Items */}
                            {filteredVideos.length > 0 ? (
                                filteredVideos.map((video) => (
                                    <div key={video._id} className="me-video-item">
                                        {editingVideoId === video._id ? (
                                            <div className="me-video-edit-form">
                                                <input
                                                    type="text"
                                                    placeholder="Video Title *"
                                                    value={editVideoTitle}
                                                    onChange={(e) => setEditVideoTitle(e.target.value)}
                                                    className="me-video-edit-title"
                                                />
                                                <textarea
                                                    placeholder="Video Description (Optional)"
                                                    value={editVideoDescription}
                                                    onChange={(e) => setEditVideoDescription(e.target.value)}
                                                    className="me-video-edit-description"
                                                    rows="2"
                                                />
                                                <select
                                                    value={editVideoFolderId}
                                                    onChange={(e) => setEditVideoFolderId(e.target.value)}
                                                    className="me-video-edit-folder"
                                                >
                                                    <option value="">Uncategorized</option>
                                                    {videoFolders.map(folder => (
                                                        <option key={folder._id} value={folder._id}>
                                                            {folder.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="me-video-edit-actions">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleUpdateVideo(video._id)}
                                                        className="me-btn-video-save"
                                                    >
                                                        <FaSave /> Save
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setEditingVideoId(null);
                                                            setEditVideoTitle('');
                                                            setEditVideoDescription('');
                                                            setEditVideoFolderId('');
                                                        }}
                                                        className="me-btn-video-cancel"
                                                    >
                                                        <FaTimes /> Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="me-video-info">
                                                    <FaVideo className="me-video-icon" />
                                                    <div className="me-video-details">
                                                        <strong>{video.title}</strong>
                                                        {video.description && <p>{video.description}</p>}
                                                        {activeFolderId === 'all' && (
                                                            <small>Folder: {getFolderName(video.folderId)}</small>
                                                        )}
                                                        <small>Uploaded: {new Date(video.uploadedAt).toLocaleDateString()}</small>
                                                        <small>Format: {video.filename?.split('.').pop()?.toUpperCase() || 'Unknown'}</small>
                                                    </div>
                                                </div>
                                                <div className="me-video-actions">
                                                    <a
                                                        href={`https://uk.backend.hms.hiradentallab.com${video.url}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="me-btn-video-play"
                                                    >
                                                        <FaPlay /> Play
                                                    </a>
                                                    <button
                                                        type="button"
                                                        onClick={() => startEditVideo(video)}
                                                        className="me-btn-video-edit"
                                                    >
                                                        <FaPencilAlt /> Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteVideo(video._id)}
                                                        className="me-btn-video-delete"
                                                    >
                                                        <FaTrash /> Delete
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="me-no-videos">
                                    <FaVideo className="me-no-videos-icon" />
                                    <p>No videos in this folder. Click "Add Video" to upload.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="me-button-group">
                        <button
                            type="button"
                            onClick={showPreviewModal}
                            className="me-btn-preview"
                            disabled={loading}
                        >
                            <MdPreview />
                            <span>Preview</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="me-btn-secondary"
                            disabled={loading}
                        >
                            <FaTimes />
                            <span>Cancel</span>
                        </button>
                        <button
                            type="submit"
                            className="me-btn-primary"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <FaSpinner className="me-spinning" />
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <>
                                    {isEditing ? <FaSave /> : <FaPlus />}
                                    <span>{isEditing ? 'Update Manual' : 'Create Manual'}</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ManualEditor;